<template>
  <b-container fluid class="bg-white px-4 py-3 custom-font-size">
    <!-- Top Alert/Status Queue Bar -->
    <div class="row mb-3" v-if="UpdateQueue.length > 0">
      <div class="col-12">
        <b-alert show variant="warning" class="d-flex align-items-center justify-content-between py-2 px-3 m-0 staffing-queue-alert">
          <span class="d-flex align-items-center">
            <b-spinner small type="grow" class="mr-2"></b-spinner>
            <strong>{{ UpdateQueue.length }} pending changes</strong>. Saving automatically in {{ countdownValue }}s...
          </span>
          <b-button variant="warning" size="sm" class="py-1 font-weight-bold" @click="updateCalendarData(false)">
            Save Now
          </b-button>
        </b-alert>
      </div>
    </div>
    
    <!-- Project Tiles Section -->
    <div class="row mb-4">
      <div class="col-12">
        <ProjectTiles
          :loading="loading"
          :projects="projects"
          :refreshData="forceUpdate"
          :updateCal="updateCalendarData" />
      </div>
    </div>

    <!-- Week Switcher Section -->
    <div class="row align-items-center py-2 mb-4 switcher-container">
      <div class="col-3 text-left pl-3">
        <b-button
          variant="outline-primary"
          size="sm"
          class="btn-switcher px-3 py-2"
          @click="prevWeek()"
          v-if="!loading">
          <b-icon icon="chevron-left" class="mr-2"></b-icon>
          Previous Week
        </b-button>
      </div>
      <div class="col-6 text-center">
        <div class="month-display d-flex align-items-center justify-content-center" @click="currentWeek()" v-if="!loading" style="cursor: pointer;" title="Click to fetch last updates">
          <b-icon icon="calendar3" class="mr-2 text-primary" font-scale="1.1"></b-icon>
          <span class="month-title">{{ monthInfo }}</span>
        </div>
        <div class="month-display text-muted" v-else>
          <b-spinner small class="mr-2" type="grow"></b-spinner>
          Loading, Please wait...
        </div>
      </div>
      <div class="col-3 text-right pr-3">
        <b-button
          variant="outline-primary"
          size="sm"
          class="btn-switcher px-3 py-2"
          @click="nextWeek()"
          v-if="!loading">
          Next Week
          <b-icon icon="chevron-right" class="ml-2"></b-icon>
        </b-button>
      </div>
    </div>

    <!-- Calendar Table Section -->
    <div class="row lizard-calendar">
      <div class="col-12 lizard-calendar-holder">
        <StaffingCalendar
          :loading="loading"
          :fields="calendarFields"
          :list="calendarData" />
      </div>
    </div>
  </b-container>
</template>

<script>
import { format as dateFormat, sub, add, differenceInDays } from 'date-fns'
import ServicesOld from '../../services/index.vue'
import StaffingCalendar from './Staffing/Calendar.vue'
import ProjectTiles from './Staffing/ProjectTiles.vue'

export default {
  name: 'StaffingLogs',
  components: {
    StaffingCalendar,
    ProjectTiles
  },
  computed: {
    Store() {
      return this.$store.getters
    },
    calendarWeeksStartDate() {
      return this.Store.staffingCalendarWeeksStartDate
    },
    UpdateQueue() {
      return this.Store.assigningQueue
    },
    LastQueueUpdate() {
      return this.Store.lastAssigningQueueUpdate
    }
  },
  data() {
    return {
      loading: false,
      countdownValue: 10,
      countdownInterval: null,
      showWeekends: true,
      dataFetched: false,
      monthInfo: dateFormat(new Date(), 'MMMM yyyy'),
      usecache: 'y',
      users: [],
      projects: [],
      calendarFields: [],
      calendarData: [],
      updateTimeout: null
    }
  },
  mounted() {
    this.tabMounted()
    this.$root.$on('make-staffing-block-empty', this.makeStaffingBlockEmpty)
    this.$root.$on('make-staffing-block-fill', this.makeStaffingBlockFill)
  },
  beforeUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
    }
  },
  methods: {
    tabMounted() {
      this.setDefaultCalendarWeeksStartDate()
      this.currentWeek()
    },
    commitToStore(commitName, value) {
      this.$store.commit(commitName, value)
    },
    prevWeek() {
      if (!this.calendarWeeksStartDate) {
        this.setDefaultCalendarWeeksStartDate()
      }
      this.commitToStore(
        'setStaffingCalendarWeeksStartDate',
        sub(new Date(this.calendarWeeksStartDate), {
          weeks: 1
        })
      )
      this.setCurrentMonth()
    },
    nextWeek() {
      if (!this.calendarWeeksStartDate) {
        this.setDefaultCalendarWeeksStartDate()
      }
      this.commitToStore(
        'setStaffingCalendarWeeksStartDate',
        add(new Date(this.calendarWeeksStartDate), {
          weeks: 1
        })
      )
      this.setCurrentMonth()
    },
    currentWeek() {
      this.setCurrentMonth()
    },
    setDefaultCalendarWeeksStartDate(setNull = false) {
      if (setNull) {
        this.commitToStore('setStaffingCalendarWeeksStartDate', null)
      } else {
        this.commitToStore('setStaffingCalendarWeeksStartDate')
      }
    },
    setCurrentMonth() {
      if (!this.calendarWeeksStartDate) {
        this.setDefaultCalendarWeeksStartDate()
      }
      const now = new Date()
      const startDate = this.calendarWeeksStartDate
      const isCurrentWeek = this.isWithinTwoWeeks(now, startDate)

      const monthsDays = this.countDaysByMonth(startDate)
      const selectedMonth = this.getFirstMonthWithMinDays(monthsDays, 7)
      const selectedYear = isCurrentWeek
        ? this.getYear(now)
        : this.getYear(startDate)
      this.monthInfo = selectedMonth + ' ' + selectedYear
    },
    isWithinTwoWeeks(date1, date2) {
      const diffInDays = differenceInDays(add(date2, { days: 13 }), date1)
      return diffInDays > 0 && diffInDays < 14
    },
    countDaysByMonth(startDate) {
      const monthsDays = {}
      const start = new Date(startDate)
      const end = add(new Date(startDate), { days: 13 })

      const loopDate = new Date(start)
      while (loopDate <= end) {
        const month = this.getMonthName(loopDate)
        monthsDays[month] = (monthsDays[month] || 0) + 1
        loopDate.setDate(loopDate.getDate() + 1)
      }

      return monthsDays
    },
    getFirstMonthWithMinDays(monthsDays, minDays) {
      for (const month in monthsDays) {
        if (monthsDays[month] >= minDays) {
          return month
        }
      }
      return null
    },
    getMonthName(date) {
      try {
        return dateFormat(date, 'MMMM')
      } catch (error) {
        console.error({ error })
        return null
      }
    },
    getYear(date) {
      try {
        return dateFormat(date, 'yyyy')
      } catch (error) {
        console.error({ error })
        return null
      }
    },
    createCSSClass(className, rules) {
      const style = document.createElement('style')
      const cssRule = `.${className} { ${rules} }`
      if (style.styleSheet) {
        style.styleSheet.cssText = cssRule
      } else {
        style.appendChild(document.createTextNode(cssRule))
      }
      document.head.appendChild(style)
    },
    addStyles(list = []) {
      return list.map((itm) => {
        // itm._rowVariant = 'light'
        itm._cellVariants = {
          name: 'primary'
        }
        return itm
      })
      // return list
    },
    forceUpdate() {
      this.usecache = 'n'
      this.$store.commit('setStaffingProjectToDrag', null)
      this.fetchCalendarData()
    },
    async fetchCalendarData() {
      this.loading = true
      if (this.UpdateQueue.length) {
        // @TDOO: ShowPROMPT
        this.commitToStore('updateAssigningQueue', {})
      }
      const { status, data } = await ServicesOld.GetStaffingCalendarForAdmin({
        fromDate: dateFormat(this.calendarWeeksStartDate, 'yyyy-MM-dd'),
        usecache: this.usecache === 'y'
      })
      if (status !== 200) {
        this.loading = false
        return
      }
      const { projects, fields, list } = data.content
      if (fields && fields.length > 0) {
        fields[0].label = 'Calendar'
      }
      this.projects = projects
      this.calendarFields = fields
      this.calendarData = this.addStyles(list)
      this.loading = false
    },
    async updateCalendarData(follow = true) {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
      if (!this.UpdateQueue.length) {
        return
      }
      // this.$store.commit('setStaffingProjectToDrag', null)
      this.loading = true
      const first10Queue = [...this.UpdateQueue.slice(0, 10)]
      const remainedQueue = [...this.UpdateQueue.slice(10)]
      const { status } = await ServicesOld.UpdateStaffingCalendarForAdmin(
        first10Queue
      )
      if (status !== 200) {
        this.forceUpdate()
        return
      }
      this.commitToStore('updateAssigningQueue', {})
      remainedQueue.forEach(({ bid: _blockId, puid: projectUid }) => {
        this.commitToStore('updateAssigningQueue', { _blockId, projectUid })
      })
      if (this.UpdateQueue.length && follow) {
        this.updateCalendarData()
      } else {
        this.forceUpdate()
      }
    },
    scheduleUpdate() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }
      this.countdownValue = 10
      this.countdownInterval = setInterval(() => {
        this.countdownValue--
        if (this.countdownValue <= 0) {
          clearInterval(this.countdownInterval)
          this.countdownInterval = null
          if (this.UpdateQueue.length > 0) {
            this.updateCalendarData()
          }
        }
      }, 1000)
    },
    makeStaffingBlockEmpty({ _blockId, listIndex, dateKey, assignmentIndex }) {
      if (assignmentIndex === -1) {
        this.calendarData[listIndex][dateKey] = [
          {
            uid: null,
            type: 'empty',
            assignment: {},
            _blockId
          }
        ]
      } else {
        this.calendarData[listIndex][dateKey][assignmentIndex].uid = null
        this.calendarData[listIndex][dateKey][assignmentIndex].type = 'empty'
        this.calendarData[listIndex][dateKey][assignmentIndex].assignment = {}
      }
    },
    makeStaffingBlockFill({ _blockId, listIndex, dateKey, projectUid }) {
      const project = this.projects.find((itm) => itm.uid === projectUid)
      const block = this.calendarData[listIndex][dateKey]
      if (block.length == 2) {
        return
      }
      this.calendarData[listIndex][dateKey] = block || []
      if (block.length === 0) {
        this.calendarData[listIndex][dateKey] = [
          {
            uid: project.uid,
            type: 'project',
            assignment: project,
            _blockId
          }
        ]
      } else {
        let halfDayIndex = block.findIndex(
          (itm) =>
            itm.type === 'holiday' &&
            ['HALF-DAY', 'HALF PERSONAL'].includes(itm?.assignment?.type)
        )
        halfDayIndex =
          halfDayIndex === -1
            ? null
            : block[halfDayIndex].assignment.meridiem === 'PM'
            ? 1
            : 0
        let wfaIndex = block.findIndex(
          (itm) =>
            itm.type === 'holiday' &&
            itm?.assignment?.type === 'WFH (OUTSIDE UK)'
        )
        wfaIndex = wfaIndex === -1 ? null : 1
        const emptyIndex =
          halfDayIndex !== null
            ? halfDayIndex
            : wfaIndex !== null
            ? wfaIndex
            : block.findIndex((itm) => itm.type === 'empty')
        if (emptyIndex !== -1) {
          const [userUid, date] = _blockId.split('_')
          const objToPush = {
            uid: project.uid,
            type: 'project',
            assignment: project,
            _blockId: `${userUid}_${date}_${emptyIndex}`
          }
          if (halfDayIndex !== null || wfaIndex !== null) {
            if (halfDayIndex || wfaIndex) {
              this.calendarData[listIndex][dateKey].unshift(objToPush)
              this.calendarData[listIndex][
                dateKey
              ][1]._blockId = `${userUid}_${date}_0`
            } else {
              this.calendarData[listIndex][dateKey].push({
                ...objToPush,
                _blockId: `${userUid}_${date}_1`
              })
            }
          } else {
            this.calendarData[listIndex][dateKey] = []
            this.calendarData[listIndex][dateKey].push(objToPush)
          }
        } else {
          this.calendarData[listIndex][dateKey] = []
          this.calendarData[listIndex][dateKey].push({
            uid: project.uid,
            type: 'project',
            assignment: project,
            _blockId
          })
        }
      }
    }
  },
  watch: {
    calendarWeeksStartDate() {
      setTimeout(() => {
        this.dataFetched = false
        this.fetchCalendarData()
      }, 500)
    },
    LastQueueUpdate() {
      if (this.UpdateQueue.length > 0) {
        this.scheduleUpdate()
      }
    }
  }
}
</script>

<style scoped>
.staffing-queue-alert {
  border-radius: 8px !important;
  font-size: 0.85rem !important;
  box-shadow: 0 4px 10px rgba(245, 158, 11, 0.15) !important;
  border: 1px solid rgba(245, 158, 11, 0.2) !important;
}
.switcher-container {
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.02);
  border: 1px solid rgba(0, 0, 0, 0.04);
}
.btn-switcher {
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  min-width: 150px;
  transition: all 0.2s ease !important;
}
.month-display {
  padding: 5px;
}
.month-title {
  font-size: 1.25rem !important;
  font-weight: 700 !important;
  color: #0073be !important;
  letter-spacing: -0.01em;
}
</style>

<style>
.custom-font-size {
  font-size: 10px !important;
}
</style>
